import { useCallback, useEffect, useMemo, useState } from "react";
import { BanknotesIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  DEFAULT_ZAHLUNGSBEDINGUNGEN_STANDARD,
  getStandardZahlungsbedingungen,
  updateStandardZahlungsbedingungen,
  ZAHLUNGSART_OPTIONS,
  type Zahlungsart,
  type ZahlungsbedingungenStandard,
} from "@/lib/kontakte";
import { toast } from "@/lib/toast";

function toForm(values: ZahlungsbedingungenStandard): ZahlungsbedingungenStandard {
  return {
    zahlungsart: values.zahlungsart === "vorkasse" ? "vorkasse" : "rechnung",
    zahlungsziel_tage: Math.max(0, Number(values.zahlungsziel_tage) || 0),
    skonto_prozent: Math.max(0, Number(values.skonto_prozent) || 0),
    skontoziel_tage: Math.max(0, Number(values.skontoziel_tage) || 0),
  };
}

function summarize(values: ZahlungsbedingungenStandard): string {
  const art = values.zahlungsart === "vorkasse" ? "Vorkasse" : "Rechnung";
  const ziel = values.zahlungsart === "vorkasse" ? "sofort" : `${values.zahlungsziel_tage} Tage`;
  const skonto =
    values.skonto_prozent > 0
      ? `${String(values.skonto_prozent).replace(".", ",")} % Skonto / ${values.skontoziel_tage} Tage`
      : "kein Skonto";
  return `${art} · ${ziel} · ${skonto}`;
}

/**
 * Firmenweite Standard-Zahlungsziele – als schmale Leiste über der Kundenliste.
 * Zeigt zusammengeklappt nur die wirksamen Werte; das Formular öffnet sich bei Bedarf.
 */
export function StandardZahlungszieleCard() {
  const [form, setForm] = useState<ZahlungsbedingungenStandard>(
    DEFAULT_ZAHLUNGSBEDINGUNGEN_STANDARD,
  );
  const [baseline, setBaseline] = useState<ZahlungsbedingungenStandard>(
    DEFAULT_ZAHLUNGSBEDINGUNGEN_STANDARD,
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = toForm(await getStandardZahlungsbedingungen());
      setForm(data);
      setBaseline(data);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Standard-Zahlungsziele konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload = toForm({
        ...form,
        zahlungsziel_tage: form.zahlungsart === "vorkasse" ? 0 : form.zahlungsziel_tage,
      });
      const saved = toForm(await updateStandardZahlungsbedingungen(payload));
      setForm(saved);
      setBaseline(saved);
      setOpen(false);
      toast.success("Standard-Zahlungsziele gespeichert.");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Standard-Zahlungsziele konnten nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setForm(baseline);
    setError(null);
    setOpen(false);
  }

  return (
    <section className="rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-fill-quaternary/60 md:px-5"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-fill-tertiary text-label-secondary">
          <BanknotesIcon className="h-[18px] w-[18px]" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-label">Standard-Zahlungsziele</span>
          <span className="block truncate text-sm text-label-secondary">
            {loading ? "Wird geladen…" : summarize(baseline)}
            <span className="hidden text-label-tertiary sm:inline">
              {" "}
              – gilt, wenn kein kunden- oder projektspezifisches Ziel hinterlegt ist
            </span>
          </span>
        </span>
        <span className="hidden text-sm font-medium text-accent sm:inline">
          {open ? "Schließen" : "Anpassen"}
        </span>
        <ChevronDownIcon
          className={clsx("h-4 w-4 shrink-0 text-label-tertiary transition", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="border-t border-separator/70 px-4 pb-4 pt-4 md:px-5">
          {error ? (
            <Alert variant="error" title="Fehler" className="mb-4">
              {error}
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SelectInput
              label="Zahlungsart"
              value={form.zahlungsart}
              disabled={loading}
              onChange={(e) => {
                const zahlungsart = e.target.value as Zahlungsart;
                setForm((f) => ({
                  ...f,
                  zahlungsart,
                  zahlungsziel_tage: zahlungsart === "vorkasse" ? 0 : f.zahlungsziel_tage,
                }));
              }}
            >
              {ZAHLUNGSART_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
            <TextInput
              label="Zahlungsziel (Tage)"
              type="number"
              min={0}
              value={String(form.zahlungsziel_tage)}
              disabled={loading || form.zahlungsart === "vorkasse"}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  zahlungsziel_tage: Math.max(0, Number(e.target.value) || 0),
                }))
              }
            />
            <TextInput
              label="Skonto %"
              type="number"
              min={0}
              step="0.01"
              value={String(form.skonto_prozent)}
              disabled={loading}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  skonto_prozent: Math.max(0, Number(e.target.value) || 0),
                }))
              }
            />
            <TextInput
              label="Skontoziel (Tage)"
              type="number"
              min={0}
              value={String(form.skontoziel_tage)}
              disabled={loading}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  skontoziel_tage: Math.max(0, Number(e.target.value) || 0),
                }))
              }
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-label-tertiary">
              Dieselben Werte erscheinen auch unter Dokumente → Layout.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={handleCancel} disabled={saving}>
                Abbrechen
              </Button>
              <Button size="sm" onClick={() => void handleSave()} disabled={saving || loading || !dirty}>
                {saving ? "Speichern…" : "Speichern"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
